import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useApi, useAuth } from "../contexts/AuthContext";
import { Layout } from "./Layout";
import { PhotoUploader } from "./media/PhotoUploader";
import { PhotoDisplay } from "./media/PhotoDisplay";

const CATEGORIES = [
  { key: "notaire", label: "Notaires" },
  { key: "avocat", label: "Avocats" },
  { key: "expert_comptable", label: "Experts comptables" },
  { key: "expert_immobilier", label: "Experts immobiliers" },
];

const emptyForm = {
  name: "",
  category: "notaire",
  email: "",
  phone: "",
  address: "",
  notes: "",
  photo: "",
};

export default function AdvisorsPage() {
  const api = useApi();
  const { isAdmin, token } = useAuth();
  const [advisors, setAdvisors] = useState([]);
  const [activeCategory, setActiveCategory] = useState("notaire");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchAdvisors = async () => {
    try {
      const res = await api.get("/advisors");
      setAdvisors(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdvisors(); }, []);

  const filtered = useMemo(
    () => advisors.filter((a) => a.category === activeCategory),
    [advisors, activeCategory]
  );

  const counts = useMemo(() => {
    const c = {};
    for (const cat of CATEGORIES) c[cat.key] = advisors.filter((a) => a.category === cat.key).length;
    return c;
  }, [advisors]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: activeCategory });
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      category: a.category || "notaire",
      email: a.email || "",
      phone: a.phone || "",
      address: a.address || "",
      notes: a.notes || "",
      photo: a.photo || "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/advisors/${editing.id}`, form);
        toast.success("Conseil modifié");
      } else {
        await api.post("/advisors", form);
        toast.success("Conseil ajouté");
      }
      setFormOpen(false);
      setEditing(null);
      setForm(emptyForm);
      fetchAdvisors();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/advisors/${confirmDeleteId}`);
      toast.success("Conseil supprimé");
      setConfirmDeleteId(null);
      fetchAdvisors();
    } catch (e) {
      console.error(e);
      toast.error("Erreur");
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl space-y-12" data-testid="advisors-page">
        <header className="flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Réseau</p>
            <h1 className="font-heading text-4xl">Conseils</h1>
            <p className="text-sm text-muted-foreground mt-2">Les experts qui accompagnent le Groupe KEMPF au quotidien.</p>
          </div>
          {isAdmin && (
            <button
              onClick={openCreate}
              className="bg-foreground text-white px-6 py-2.5 uppercase tracking-wider text-sm hover:bg-foreground/90"
              data-testid="advisor-add-btn"
            >
              + Ajouter un conseil
            </button>
          )}
        </header>

        {CATEGORIES.map((cat) => {
          const items = advisors.filter((a) => a.category === cat.key);
          return (
            <section key={cat.key} data-testid={`advisor-section-${cat.key}`}>
              <div className="flex items-baseline justify-between mb-6 pb-3 border-b border-border">
                <div>
                  <h2 className="font-heading text-2xl">{cat.label}</h2>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                    {items.length} {items.length > 1 ? "structures" : "structure"}
                  </p>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="bg-white border border-dashed border-border p-8 text-center">
                  <p className="text-sm text-muted-foreground italic">Aucun {cat.label.toLowerCase().slice(0, -1)} enregistré</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((a) => (
                    <div key={a.id} className="bg-white border border-border p-6 flex flex-col gap-3" data-testid={`advisor-card-${a.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 flex items-center gap-3">
                          {a.photo && (
                            <PhotoDisplay url={a.photo} className="w-12 h-12 object-contain flex-shrink-0 border border-border bg-white p-1" data-testid={`advisor-logo-${a.id}`} />
                          )}
                          <p className="font-heading text-lg leading-tight truncate">{a.name}</p>
                        </div>
                      </div>
                      <div className="text-sm space-y-1 flex-1">
                        {a.email && <p className="truncate"><span className="text-muted-foreground text-[10px] uppercase tracking-wider mr-2">Email</span>{a.email}</p>}
                        {a.phone && <p><span className="text-muted-foreground text-[10px] uppercase tracking-wider mr-2">Tél.</span>{a.phone}</p>}
                        {a.address && <p className="text-xs"><span className="text-muted-foreground text-[10px] uppercase tracking-wider mr-2">Adresse</span>{a.address}</p>}
                        {a.notes && <p className="text-xs text-muted-foreground mt-2 italic line-clamp-2">{a.notes}</p>}
                      </div>
                      {isAdmin && (
                        <div className="flex gap-3 pt-3 border-t border-border justify-end">
                          <button onClick={() => openEdit(a)} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid={`advisor-edit-${a.id}`}>Modifier</button>
                          <button onClick={() => setConfirmDeleteId(a.id)} className="text-xs uppercase tracking-wider text-destructive hover:text-destructive/80" data-testid={`advisor-delete-${a.id}`}>Supprimer</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Form modal */}
      {formOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setFormOpen(false)}>
          <form
            className="bg-white max-w-lg w-full p-8 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            data-testid="advisor-form"
          >
            <h2 className="font-heading text-2xl">{editing ? "Modifier le conseil" : "Ajouter un conseil"}</h2>
            <div>
              <label className="label-uppercase block mb-1">Catégorie *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-input px-3 py-2 bg-white cursor-pointer focus:border-primary focus:outline-none"
                style={{ appearance: "auto", WebkitAppearance: "auto", MozAppearance: "auto" }}
                data-testid="advisor-form-category"
              >
                <option value="notaire">Notaire</option>
                <option value="avocat">Avocat</option>
                <option value="expert_comptable">Expert comptable</option>
                <option value="expert_immobilier">Expert immobilier</option>
              </select>
            </div>
            <div>
              <label className="label-uppercase block mb-1">Nom du cabinet / société *</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="advisor-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-uppercase block mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="advisor-form-email" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Téléphone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="advisor-form-phone" />
              </div>
            </div>
            <div>
              <label className="label-uppercase block mb-1">Adresse</label>
              <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="advisor-form-address" />
            </div>
            <div>
              <label className="label-uppercase block mb-1">Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" data-testid="advisor-form-notes" />
            </div>
            <PhotoUploader label="Logo" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} token={token} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setFormOpen(false)}
                className="border border-border px-4 py-2 text-sm hover:bg-muted">Annuler</button>
              <button type="submit" className="bg-foreground text-white px-6 py-2 text-sm uppercase tracking-wider hover:bg-foreground/90" data-testid="advisor-form-submit">
                {editing ? "Enregistrer" : "Ajouter"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="advisor-delete-confirm">
            <h3 className="font-heading text-xl mb-2">Confirmer la suppression ?</h3>
            <p className="text-sm text-muted-foreground mb-5">Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={doDelete} className="flex-1 bg-red-800 text-white px-4 py-2 text-sm uppercase" data-testid="advisor-delete-confirm-btn">Supprimer</button>
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 border border-border px-4 py-2 text-sm">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
